/**
 * Planificador — borradores → aprobación. Nunca ejecuta solo.
 */
import { RenataFeatureGate } from "@/components/renata/RenataFeatureGate";
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Map, RefreshCw, Sparkles, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { PLAN_STATUS_ES, labelStatus } from "@/lib/renata/experience/status-labels";
import { toast } from "sonner";

export const Route = createFileRoute("/app/renata/planning")({
  component: PlanningPage,
  head: () => ({ meta: [{ title: "Planificador — Renata" }] }),
});

function humanizeExplanation(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const parts = [
      o.summary,
      o.rationale,
      o.why,
      o.nextStep,
      o.next_step,
      Array.isArray(o.steps) ? `Pasos: ${(o.steps as string[]).slice(0, 5).join(" · ")}` : null,
    ].filter(Boolean);
    if (parts.length) return parts.map(String).join("\n\n");
  }
  return "Plan generado. Revisá la checklist y pedí aprobación antes de ejecutar.";
}

function PlanningPage() {
  const tenantId = useWorkspaceTenantId();
  const [drafts, setDrafts] = useState<Array<Record<string, unknown>>>([]);
  const [message, setMessage] = useState("Quiero preparar el Black Friday");
  const [plan, setPlan] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastApprovedId, setLastApprovedId] = useState<string | null>(null);

  async function load() {
    if (!tenantId) return;
    const res = await fetch("/api/renata/planning?view=drafts", {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    });
    if (res.ok) {
      const d = await res.json();
      setDrafts(d.drafts ?? []);
    }
  }

  useEffect(() => {
    void load();
  }, [tenantId]);

  async function generate() {
    if (!tenantId) return;
    setLoading(true);
    setLastApprovedId(null);
    try {
      const res = await fetch("/api/renata/planning", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "plan", message }),
      });
      const d = await res.json();
      if (!res.ok) {
        toast.error(String(d.error ?? "No se pudo generar el plan"));
        return;
      }
      setPlan(d.plan ?? null);
      toast.success("Borrador listo. Todavía no se ejecutó nada — falta tu aprobación.");
      await load();
    } finally {
      setLoading(false);
    }
  }

  async function setStatus(id: string, status: string) {
    if (!tenantId) return;
    const res = await fetch("/api/renata/planning", {
      method: "POST",
      credentials: "include",
      headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
      body: JSON.stringify({ action: "draft_status", id, status }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast.error(String((d as { error?: string }).error ?? "No se pudo actualizar"));
      return;
    }
    if (status === "approved") {
      setLastApprovedId(id);
      const missionCount = Array.isArray((d as { missionIds?: string[] }).missionIds)
        ? (d as { missionIds: string[] }).missionIds.length
        : 0;
      toast.success(
        missionCount > 0
          ? "Plan activo. Se creó el plan de trabajo con tareas para revisar."
          : "Plan activo.",
        {
          action: {
            label: "Ver plan de trabajo",
            onClick: () => {
              window.location.assign("/app/renata/missions");
            },
          },
        },
      );
    } else {
      toast.message(status === "cancelled" ? "Plan cancelado" : "Estado actualizado");
    }
    await load();
  }

  return (
    <RenataFeatureGate featureId="planning">
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <RenataPageChrome
          screenId="planning"
          title="Planificador"
          subtitle="Armá un borrador de plan. Renata propone; vos aprobás. Nunca ejecuta solo."
        />
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualizar
        </Button>
      </div>

      <div className="rounded-xl border p-4 space-y-3">
        <label className="text-xs font-medium text-muted-foreground">¿Qué querés lograr?</label>
        <textarea
          className="w-full rounded-lg border bg-background px-3 py-2 text-sm min-h-[72px]"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Button onClick={() => void generate()} disabled={loading} className="bg-violet-600 hover:bg-violet-700">
          <Sparkles className="h-3.5 w-3.5 mr-1" />
          {loading ? "Generando…" : "Generar borrador de plan"}
        </Button>
      </div>

      {plan && (
        <div className="rounded-xl border p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold">{String(plan.title)}</h2>
            <Badge variant="secondary">{String(plan.kind)}</Badge>
            <Badge>Requiere aprobación</Badge>
          </div>
          <p className="text-sm text-foreground/80">{String(plan.objective)}</p>
          <div className="grid gap-2 sm:grid-cols-3 text-xs">
            <div className="rounded-lg bg-muted/50 p-2">Impacto estimado: {String(plan.estimatedImpact)}</div>
            <div className="rounded-lg bg-muted/50 p-2">Días estimados: {String(plan.estimatedDays)}</div>
            <div className="rounded-lg bg-muted/50 p-2">Riesgo: {String(plan.risk)}</div>
          </div>
          <div>
            <p className="text-xs font-medium mb-1">Checklist</p>
            <ul className="text-sm list-disc pl-5 space-y-0.5">
              {(plan.checklist as string[] | undefined)?.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
          {plan.explanation ? (
            <div className="rounded-lg bg-card border border-border p-3 text-sm whitespace-pre-wrap">
              {humanizeExplanation(plan.explanation)}
            </div>
          ) : null}
          <div className="rounded-lg border border-dashed p-3 text-xs space-y-1">
            <p className="font-medium">Qué esperar ahora</p>
            <p>1. El borrador queda en “Pendiente de aprobación” abajo.</p>
            <p>2. Aprobá el plan (no ejecuta todavía).</p>
            <p>3. Andá a <a className="underline text-violet-700" href="/app/renata/missions">Planes de trabajo</a> para lanzar las acciones una por una.</p>
          </div>
        </div>
      )}

      {lastApprovedId && (
        <div className="rounded-xl border border-emerald-300/50 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 text-sm space-y-2">
          <p className="font-medium">Plan aprobado</p>
          <p className="text-muted-foreground">
            No hay ejecución automática. Tu siguiente paso es abrir Planes de trabajo y arrancar el plan correspondiente.
          </p>
          <Button size="sm" asChild>
            <a href="/app/renata/missions">Ir a Planes de trabajo</a>
          </Button>
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-sm font-semibold">Borradores pendientes</h2>
        {drafts.length === 0 && (
          <p className="text-sm text-foreground/80">No hay borradores aún. Generá uno arriba.</p>
        )}
        {drafts.map((d) => (
          <div key={String(d.id)} className="flex items-center gap-3 rounded-lg border px-3 py-2">
            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{String(d.title)}</p>
              <p className="text-xs text-foreground/80">
                {labelStatus(PLAN_STATUS_ES, String(d.status))} · {String(d.kind)}
              </p>
            </div>
            {String(d.status) === "pending_approval" && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => void setStatus(String(d.id), "approved")}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Aprobar y activar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void setStatus(String(d.id), "cancelled")}>
                  Cancelar
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
    </RenataFeatureGate>
  );
}
